import React, { useMemo, useCallback, useEffect, useRef, useState } from 'react';
import type { Conversation } from '../services/apiClient';
import '../styles/PlaybookCommon.css';
import '../styles/ChatHistorySidebar.css';

// Helper component for scrolling text on overflow
const ScrollingText: React.FC<{ text: string; className?: string }> = ({ text, className }) => {
  const textRef = useRef<HTMLSpanElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const checkOverflow = () => {
      if (textRef.current) {
        setIsOverflowing(textRef.current.scrollWidth > textRef.current.clientWidth);
      }
    };
    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [text]);

  return (
    <div className="chat-history-name-wrapper">
      <span
        ref={textRef}
        className={`${className || ''} ${isOverflowing ? 'is-overflowing' : ''}`}
      >
        {text}
      </span>
    </div>
  );
};

interface ChatHistorySidebarProps {
  conversations: Conversation[];
  currentConversationId: string | null;
  onSelectConversation: (id: string, name: string) => void;
  onNewConversation: () => void;
  onDeleteConversation: (id: string) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  isLoading?: boolean;
  onLoadConversations?: () => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

interface ConversationGroup {
  label: string;
  conversations: Conversation[];
}

export const ChatHistorySidebar: React.FC<ChatHistorySidebarProps> = ({
  conversations,
  currentConversationId,
  onSelectConversation,
  onNewConversation,
  onDeleteConversation,
  searchQuery,
  onSearchChange,
  isLoading = false,
  onLoadConversations,
  isCollapsed = false,
  onToggleCollapse
}) => {
  // Track if conversations have been loaded (mount-only guard)
  const loadedRef = useRef(false);

  // Load conversations on mount
  useEffect(() => {
    if (onLoadConversations && !loadedRef.current) {
      loadedRef.current = true;
      onLoadConversations();
    }
  }, [onLoadConversations]);

  // Filter conversations based on search query
  const filteredConversations = useMemo(() => {
    if (!searchQuery.trim()) {
      return conversations;
    }
    const query = searchQuery.toLowerCase();
    return conversations.filter(conv =>
      conv.name.toLowerCase().includes(query)
    );
  }, [conversations, searchQuery]);

  // Group conversations by date
  const groupedConversations = useMemo((): ConversationGroup[] => {
    const groups: ConversationGroup[] = [];
    const groupMap: Record<string, Conversation[]> = {};

    const now = new Date();

    const getDaysDiff = (date: Date): number => {
      const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      const nowOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const diffTime = nowOnly.getTime() - dateOnly.getTime();
      return Math.floor(diffTime / (1000 * 3600 * 24));
    };

    filteredConversations.forEach(conv => {
      const conversationDate = new Date(conv.created_at);
      const diffDays = getDaysDiff(conversationDate);

      let label: string;
      if (diffDays === 0) {
        label = "Aujourd'hui";
      } else if (diffDays === 1) {
        label = 'Hier';
      } else if (diffDays < 7) {
        label = `Il y a ${diffDays} jours`;
      } else {
        label = 'Plus ancien';
      }

      if (!groupMap[label]) {
        groupMap[label] = [];
      }
      groupMap[label].push(conv);
    });

    const labelOrder = ["Aujourd'hui", 'Hier'];
    const sortedLabels = Object.keys(groupMap).sort((a, b) => {
      const indexA = labelOrder.indexOf(a);
      const indexB = labelOrder.indexOf(b);

      if (indexA !== -1 && indexB !== -1) return indexA - indexB;
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;

      if (a.startsWith('Il y a') && b.startsWith('Il y a')) {
        return parseInt(a.split(' ')[2]) - parseInt(b.split(' ')[2]);
      }
      if (a.startsWith('Il y a')) return -1;
      if (b.startsWith('Il y a')) return 1;

      return a === 'Plus ancien' ? 1 : -1;
    });

    sortedLabels.forEach(label => {
      groups.push({ label, conversations: groupMap[label] });
    });

    return groups;
  }, [filteredConversations]);

  // Format relative time for conversation
  const formatTime = useCallback((conversation: Conversation): string => {
    if (conversation.id === currentConversationId) {
      return 'actuelle';
    }

    const now = new Date();
    const conversationDate = new Date(conversation.created_at);
    const diffMs = now.getTime() - conversationDate.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

    if (diffHours >= 24) {
      return '';
    }
    if (diffHours >= 1) {
      return `${diffHours}h`;
    }
    return `${diffMinutes}m`;
  }, [currentConversationId]);

  return (
    <div className={`playbook-sidebar chat-history-sidebar ${isCollapsed ? 'collapsed' : ''}`}>
      <div className="playbook-sidebar-header">
        <h2>Conversations</h2>
        <div className="chat-history-header-actions">
          <button
            type="button"
            className="playbook-add-btn"
            onClick={onNewConversation}
            title="Nouvelle conversation"
            aria-label="Nouvelle conversation"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            <span>Nouvelle</span>
          </button>
          {onToggleCollapse && (
            <button
              type="button"
              className="chat-sidebar-toggle"
              onClick={onToggleCollapse}
              title={isCollapsed ? "Ouvrir l'historique" : 'Réduire le panneau'}
              aria-label={isCollapsed ? "Ouvrir l'historique" : 'Réduire le panneau'}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                {isCollapsed ? (
                  <polyline points="9 18 15 12 9 6" />
                ) : (
                  <polyline points="15 18 9 12 15 6" />
                )}
              </svg>
            </button>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="playbook-list-loading">
          <div className="loading-spinner"></div>
        </div>
      ) : (
        <div className="playbook-list">
          <div className="playbook-list-header">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Rechercher..."
              className="playbook-search-input"
              aria-label="Rechercher dans les conversations"
            />
            <svg className="search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
          </div>

          <div className="playbook-list-items">
            {groupedConversations.map((group) => (
              <div key={group.label} className="chat-history-section">
                <div className="chat-history-section-header">{group.label}</div>
                {group.conversations.map((conversation) => (
                  <div
                    key={conversation.id}
                    className={`playbook-list-item ${currentConversationId === conversation.id ? 'active' : ''}`}
                    aria-current={currentConversationId === conversation.id ? 'true' : undefined}
                    onClick={() => onSelectConversation(conversation.id, conversation.name)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onSelectConversation(conversation.id, conversation.name);
                      }
                    }}
                    tabIndex={0}
                    role="button"
                  >
                    <ScrollingText text={conversation.name} className="playbook-item-name" />
                    <div className="playbook-item-actions">
                      <span className="chat-history-time">{formatTime(conversation)}</span>
                      <button
                        type="button"
                        className="playbook-item-delete-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteConversation(conversation.id);
                        }}
                        title="Supprimer"
                        aria-label={`Supprimer ${conversation.name}`}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M3 6h18"></path>
                          <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                          <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                        </svg>
                      </button>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="chevron-icon">
                        <polyline points="9 18 15 12 9 6"></polyline>
                      </svg>
                    </div>
                  </div>
                ))}
              </div>
            ))}

            {filteredConversations.length === 0 && conversations.length > 0 && (
              <div className="playbook-list-empty">
                Aucun résultat pour «{searchQuery}»
              </div>
            )}

            {conversations.length === 0 && (
              <div className="playbook-list-empty">
                Aucune conversation
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
